<script setup lang="ts">
import { useMutation, useQuery } from "@tanstack/vue-query";
import { cloneDeep } from "lodash";
import { NSpin, NSplit, NTooltip, useMessage } from "naive-ui";
import { computed, ref, watch } from "vue";
import type {
    BankAccount,
    OpenItemChangelog,
    OpenItemComment,
    OpenItemPostPayload,
    OpenItemPostPayloadResolve,
    OpenItemPostPayloadUnResolve,
} from "../../models/contador";
import {
    getFullOpenItem,
    postOpenItem,
    resolveOpenItem,
    unResolveOpenItem,
} from "../../services/contador/open-items.service";
import OpenItemDetailHeader from "./OpenItemDetailHeader.vue";
import OpenItemDetailHistory from "./OpenItemDetailHistory.vue";
import OpenItemDetailSider from "./OpenItemDetailSider.vue";
import OpenItemMessageComposer from "./OpenItemMessageComposer.vue";
import { searchBankAccountsV2 } from "../../services/contador/bank-accounts.service.ts";
import { usePortalBreakpoints } from "../../hooks/usePortalBreakpoints.ts";
import { searchPropertiesV2 } from "../../services/contador/properties.service.ts";
import { getUserEmail } from "../../config/firebase.ts";

const props = defineProps<{
    openItemId: string;
}>();

const emit = defineEmits<{
    refetchOpenItems: [];
    close: [];
}>();

const message = useMessage();
const { isDesktop } = usePortalBreakpoints();

const pane1Style = computed(() => (isDesktop.value ? {} : { flex: "none", height: "auto" }));
const pane2Style = computed(() => (isDesktop.value ? {} : { flex: "none", height: "auto" }));

const {
    data: openItem,
    isLoading,
    error,
    refetch: refetchOpenItem,
} = useQuery({
    queryKey: computed(() => ["open-item", props.openItemId]),
    queryFn: () => getFullOpenItem(props.openItemId),
    enabled: computed(() => !!props.openItemId),
});

/**
 * groups and sort the history by day, then by time within each day
 */
const history = computed<
    {
        date: number;
        items: (OpenItemComment | OpenItemChangelog)[];
    }[]
>(() => {
    if (!openItem.value) return [];
    const comments = cloneDeep(openItem.value).comments || [];
    let changelog = cloneDeep(openItem.value).changelog || [];

    // Add synthetic creation event as the oldest changelog item
    if (openItem.value.createdAt) {
        const creationEvent: OpenItemChangelog = {
            id: `creation-${openItem.value.id}`,
            createdAt: openItem.value.createdAt,
            updatedAt: openItem.value.createdAt,
            deletedAt: null,
            open_item_id: openItem.value.id,
            author: openItem.value.created_by || "Unknown",
            field: "",
            from: "",
            to: "created",
        };
        changelog = [creationEvent, ...changelog];
    }

    // Combine and sort all items by date ascending
    const allItems = [
        ...comments.map((comment) => ({
            ...comment,
            _resolved_comment: false,
            date: new Date(comment.createdAt),
        })),
        ...changelog
            .map((change) => ({
                ...change,
                date: new Date(change.createdAt),
            }))
            .filter((x) => x?.field !== "resolved_in_ticket_id"), // Exclude resolved_in_ticket_id changes
    ];

    allItems.sort((a, b) => a.date.getTime() - b.date.getTime());

    // Mark _resolved_comment for comments that enabled resolution
    let lastCommentIdx: number | null = null;
    for (let i = 0; i < allItems.length; i++) {
        const item = allItems[i] as any;
        if ("content" in item) {
            lastCommentIdx = i;
        } else if (
            item.field === "status" &&
            item.from === "open" &&
            item.to === "resolved" &&
            lastCommentIdx !== null
        ) {
            (allItems as any)[lastCommentIdx]._resolved_comment = true;
            lastCommentIdx = null;
        }
    }

    // Sort by date descending for display
    allItems.sort((a, b) => b.date.getTime() - a.date.getTime());

    // Group by local date
    const groups = new Map<string, any[]>();
    allItems.forEach((item) => {
        const d = item.date;
        const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
            d.getDate(),
        ).padStart(2, "0")}`;
        if (!groups.has(dateKey)) {
            groups.set(dateKey, []);
        }
        groups.get(dateKey)!.push(item);
    });

    const result = Array.from(groups.entries()).map(([dateKey, items]) => {
        const [year, month, day] = dateKey.split("-").map(Number);
        const localMidnight = new Date(year, month - 1, day).getTime();
        items.sort((a, b) => b.date.getTime() - a.date.getTime());
        return {
            date: localMidnight,
            items: items.map(({ ...rest }) => rest),
        };
    });

    result.sort((a, b) => b.date - a.date);
    return result;
});

const isResolved = computed(() => openItem.value?.status?.toLowerCase() === "resolved");
const buttonText = computed(() => (isResolved.value ? "Mark as Open" : "Mark as Resolved"));

// Check if the last event in history is a comment from the current user
const canResolve = computed(() => {
    if (isResolved.value) return true;

    const allItems = history.value.flatMap((group) => group.items);
    if (allItems.length === 0) return false;

    const lastItem = allItems[0];

    return "content" in lastItem && lastItem.author === getUserEmail();
});

const { mutate: resolveOpenItemMutation, isPending: isResolving } = useMutation({
    mutationFn: async (py: OpenItemPostPayloadResolve) => await resolveOpenItem(py),
    onSuccess() {
        message.success("Resolved successfully");
        emit("refetchOpenItems");
        refetchOpenItem();
    },
    onError(error: any) {
        message.error(`Failed to resolve: ${error?.message || error}`);
    },
});

const { mutate: unResolveOpenItemMutation, isPending: isUnresolving } = useMutation({
    mutationFn: async (py: OpenItemPostPayloadUnResolve) => await unResolveOpenItem(py),
    onSuccess() {
        message.success("Opened successfully");
        emit("refetchOpenItems");
        refetchOpenItem();
    },
    onError(error: any) {
        message.error(`Failed to Open: ${error?.message || error}`);
    },
});

/**
 * Fetches the bank account and properties related to the open item.
 * Provides options for the property and bank account select inputs
 */
function useBankAndProperty() {
    const bankAccountId = computed<string | null>(() => {
        return openItem.value?.bank_account_id || null;
    });

    const ticketBankAccount = ref<BankAccount | null>(null);
    const { mutate: mutateBankAccount, isPending: isLoadingBankAccount } = useMutation({
        mutationFn: async (bankId: string) => {
            const res = await searchBankAccountsV2({ where: { id: { equals: bankId } }, limit: 1 });
            return res?.items?.[0] ?? null;
        },
        onSuccess(data) {
            ticketBankAccount.value = data;
        },
        onError(error) {
            console.error("Error fetching bank account data:", error);
            message.error(`Failed to fetch bank account: ${error.message || error}`);
            ticketBankAccount.value = null;
        },
    });

    watch(
        bankAccountId,
        (v) => {
            if (v) {
                mutateBankAccount(v);
            }
        },
        {
            immediate: true,
        },
    );

    const propertiesIds = computed<string[]>(() => {
        return ticketBankAccount.value?.propertyBankAccounts?.map((p) => p.propertyId) ?? [];
    });

    const { data: properties, isLoading: isLoadingProperties } = useQuery({
        queryKey: computed(() => ["properties-by-ids-v2", propertiesIds.value]),
        queryFn: async () => {
            if (propertiesIds.value.length === 0) return [] as Array<any>;
            const res = await searchPropertiesV2({ where: { id: { in: propertiesIds.value } } });
            return res?.items ?? [];
        },
        enabled: computed(() => propertiesIds.value.length > 0),
    });

    const { data: allProperties, isLoading: isLoadingAllProperties } = useQuery({
        queryKey: computed(() => ["all-properties-v2", openItem.value?.customerId]),
        queryFn: async () => {
            const customerId = openItem.value?.customerId;
            if (!customerId) return [] as any[];
            const limit = 200;
            let offset = 0;
            let all: any[] = [];
            let hasMore = true;
            while (hasMore) {
                const res = await searchPropertiesV2({
                    where: { properScope: { equals: true } },
                    orderBy: { updatedAt: "desc" },
                    limit,
                    offset,
                });
                const items = res?.items ?? [];
                all = all.concat(items.filter((p: any) => p.customerId === customerId));
                hasMore = items.length === limit;
                offset += limit;
            }
            return all;
        },
        enabled: computed(() => !!openItem.value?.customerId),
    });

    const UNKNOWN_PROPERTY_ID = "unknown";

    const propertyOptions = computed(() => {
        const associated = (properties.value ?? []).map((p: any) => ({
            label: p.name,
            value: p.id,
        }));
        const inScope = (allProperties.value ?? [])
            .filter((p: any) => !(properties.value ?? []).some((ap: any) => ap.id === p.id))
            .map((p: any) => ({ label: p.name, value: p.id }));
        const unknown = { label: "Unknown", value: UNKNOWN_PROPERTY_ID };
        if (associated.length > 0) {
            return [
                ...associated,
                {
                    label: "_________________________",
                    value: "___",
                    disabled: true,
                },
                unknown,
                ...inScope,
            ];
        } else {
            return [unknown, ...inScope];
        }
    });

    return {
        UNKNOWN_PROPERTY_ID,
        propertyOptions,
        isLoadingProperties,
        isLoadingAllProperties,
        isLoadingBankAccount,
    };
}
const {
    UNKNOWN_PROPERTY_ID,
    propertyOptions,
    isLoadingProperties,
    isLoadingAllProperties,
    isLoadingBankAccount,
} = useBankAndProperty();

function handleResolveOpenItem() {
    if (!openItem.value) {
        message.error("Not found");
        return;
    }

    const item = openItem.value;
    const isCurrentlyResolved = item.status?.toLowerCase() === "resolved";

    const partialPayload = {
        id: item.id,
        customerId: item.customerId,
        bank_account_id: item.bank_account_id,
        property_id: item.property_id,
        date: item.date,
        amount: item.amount,
        description: item.description,
        source: item.source,
        notes: item.notes,
        reference_number: item.reference_number,
        reported_in_ticket_id: item.reported_in_ticket_id,
    } as any;

    if (isCurrentlyResolved) {
        const fullPy = {
            ...partialPayload,
            resolved_in_ticket_id: null,
            status: "open",
        } as OpenItemPostPayloadUnResolve;
        unResolveOpenItemMutation(fullPy);
    } else {
        const fullPy = {
            ...partialPayload,
            resolved_in_ticket_id: item.reported_in_ticket_id,
            status: "resolved",
        } as OpenItemPostPayloadResolve;
        resolveOpenItemMutation(fullPy);
    }
}

const { mutate: postOpenItemMutation } = useMutation({
    mutationFn: postOpenItem,
    onSuccess() {
        message.success("Item Updated successfully");
        refetchOpenItem();
        emit("refetchOpenItems");
    },
    onError(error: any) {
        message.error(`Failed to Update Item: ${error?.message || error}`);
    },
});

function onSubmitOpenItem<K extends keyof OpenItemPostPayload>(
    field: K,
    value: OpenItemPostPayload[K],
) {
    if (!openItem.value) return;

    const payload: OpenItemPostPayload = {
        id: props.openItemId,
        customerId: openItem.value.customerId as string,
        bank_account_id: openItem.value.bank_account_id as string,
        property_id: openItem.value.property_id as string,
        date: openItem.value.date as string,
        amount: openItem.value.amount as number,
        description: openItem.value.description as string,
        source: openItem.value.source as "pms" | "bank",
        notes: openItem.value.notes,
        status: openItem.value.status as "resolved" | "open",
        reference_number: openItem.value.reference_number,
        reported_in_ticket_id: openItem.value.reported_in_ticket_id as string,
        resolved_in_ticket_id: openItem.value.resolved_in_ticket_id as string,
    };

    payload[field] = value;

    postOpenItemMutation(payload);
}
</script>

<template>
    <div class="h-full flex flex-col">
        <div v-if="isLoading" class="flex-1 flex justify-center items-center">
            <n-spin size="large" />
            <span class="ml-3 text-gray-600">Loading open item...</span>
        </div>

        <div v-else-if="error" class="flex-1 flex justify-center items-center">
            <div class="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 class="text-lg font-medium text-red-800 mb-2">Error Loading Open Item</h3>
                <p class="text-red-600">{{ error.message || "Failed to load open item" }}</p>
            </div>
        </div>
        <div v-else-if="openItem" class="contents">
            <open-item-detail-header
                :open-item="openItem"
                @update:field="onSubmitOpenItem"
                @close="emit('close')"
            />

            <div class="flex flex-1 min-h-0" :class="{ 'overflow-y-auto flex-col': !isDesktop }">
                <n-split
                    :direction="isDesktop ? 'horizontal' : 'vertical'"
                    :disabled="!isDesktop"
                    :max="0.75"
                    :min="0.25"
                    :default-size="0.3"
                    :resize-trigger-size="1"
                    :style="isDesktop ? 'height:100%' : ''"
                    :pane1-style="pane1Style"
                    :pane2-style="pane2Style"
                >
                    <template #1>
                        <div
                            v-if="
                                isLoadingProperties ||
                                isLoadingAllProperties ||
                                isLoadingBankAccount
                            "
                            class="flex justify-center items-center py-6"
                            :class="{ 'h-full flex-1': isDesktop }"
                        >
                            <n-spin size="large" />
                        </div>
                        <open-item-detail-sider
                            v-else
                            :open-item="openItem"
                            :unknown-property-id="UNKNOWN_PROPERTY_ID"
                            :property-options="propertyOptions"
                            @update:field="onSubmitOpenItem"
                            :class="isDesktop ? 'h-full' : ''"
                        />
                    </template>

                    <template #2>
                        <div class="w-full flex flex-col min-h-0" :class="{ 'h-full': isDesktop }">
                            <div :class="isDesktop ? 'flex-1 overflow-y-auto' : ''">
                                <div class="p-4 space-y-4">
                                    <open-item-message-composer
                                        :open-item="openItem"
                                        @comment-added="
                                            emit('refetchOpenItems');
                                            refetchOpenItem();
                                        "
                                    >
                                        <!-- Mobile resolution button  -->
                                        <n-tooltip
                                            v-if="!isDesktop"
                                            :disabled="canResolve || isResolved"
                                            trigger="hover"
                                        >
                                            <template #trigger>
                                                <button
                                                    class="button"
                                                    :class="{
                                                        'bg-yellow-500 hover:bg-yellow-600':
                                                            isResolved,
                                                        'bg-green-500 hover:bg-green-600':
                                                            !isResolved && canResolve,
                                                        'bg-gray-400 cursor-not-allowed':
                                                            !isResolved && !canResolve,
                                                        'button-mobile': !isDesktop,
                                                    }"
                                                    :disabled="
                                                        isResolving ||
                                                        isUnresolving ||
                                                        (!isResolved && !canResolve)
                                                    "
                                                    @click="handleResolveOpenItem"
                                                >
                                                    {{ buttonText }}
                                                </button>
                                            </template>
                                            You need to add a message for resolution
                                        </n-tooltip>
                                    </open-item-message-composer>
                                    <open-item-detail-history
                                        :history="history"
                                        :property-options="propertyOptions"
                                        :class="{
                                            '!mt-10': !isDesktop,
                                        }"
                                        @comment-edited="
                                            emit('refetchOpenItems');
                                            refetchOpenItem();
                                        "
                                    />
                                </div>
                            </div>
                            <!-- Desktop resolution button footer -->
                            <div
                                v-if="isDesktop"
                                class="border-t border-border-gray flex justify-end p-4 flex-shrink-0 gap-2"
                            >
                                <n-tooltip :disabled="canResolve || isResolved" trigger="hover">
                                    <template #trigger>
                                        <button
                                            class="button"
                                            :class="{
                                                'bg-yellow-500 hover:bg-yellow-600': isResolved,
                                                'bg-green-500 hover:bg-green-600':
                                                    !isResolved && canResolve,
                                                'bg-gray-400 cursor-not-allowed':
                                                    !isResolved && !canResolve,
                                            }"
                                            :disabled="
                                                isResolving ||
                                                isUnresolving ||
                                                (!isResolved && !canResolve)
                                            "
                                            @click="handleResolveOpenItem"
                                        >
                                            {{ buttonText }}
                                        </button>
                                    </template>
                                    You need to add a message for resolution
                                </n-tooltip>
                            </div>
                        </div>
                    </template>

                    <template v-if="isDesktop" #resize-trigger>
                        <div
                            class="h-full flex justify-center items-center bg-cream hover:bg-gray-300 group"
                        >
                            <div
                                class="w-10 h-8 bg-cream rounded-full flex items-center justify-center text-gray-400 group-hover:bg-gray-300 group-hover:text-white"
                            >
                                <span
                                    class="material-symbols-outlined text-[18px]"
                                    style="transition: transform 0.2s"
                                >
                                    swap_horiz
                                </span>
                            </div>
                        </div>
                    </template>
                </n-split>
            </div>
        </div>
    </div>
</template>
